import GS from '../../grafsync/grafsync';
import { DATATYPES, LANGS, DATATYPE_LIMITS, DATATYPE_LABELS, DEFAULT_LANG, STATUS_ACCEPTED, STATUS_REJECTED } from './constants';
import MoodIcon from '@material-ui/icons/Mood';
import MoodBadIcon from '@material-ui/icons/MoodBad';
import Deprecated from '../../core/Deprecated';
import XL from '../../vocabulary/core/XL';
import Tooltip from '@material-ui/core/Tooltip';

const OBO_NAMESPACE = "http://purl.obolibrary.org/obo/";

export function computeConceptIcon(data, useWorkflow = false) {
  let icon;
  if (data.deprecated) {
    icon = <Deprecated />;
  }
  else if (useWorkflow && "status" in data && data.status[0] && data.status[0] === STATUS_ACCEPTED) {
    icon = <Tooltip title="Approved">
      <span data-test="approved-icon"
        style={{
          textAlign: 'center',
          display: 'inline-block',
          paddingTop: '4px'
        }}>
        <MoodIcon fontSize="small" />
      </span>
    </Tooltip>;
  }
  else if (useWorkflow && "status" in data && data.status[0] && data.status[0] === STATUS_REJECTED) {
    icon = <Tooltip title="Rejected">
      <span data-test="rejected-icon"
        style={{
          textAlign: 'center',
          display: 'inline-block',
          paddingTop: '4px'
        }}>
        <MoodBadIcon fontSize="small" style={{ color: '#DD2C00' }} />
      </span>
    </Tooltip>;
  }
  else if (data.isEntailed) {
    icon = <XL />;
  }
  return icon;
}

// isDefinedBy needs to match the baseIRI of the ontology IRI minus any trailing hash or slash
// or the resource IRI needs to match the baseIRI
export function isInOntology(resourceIRI, isDefinedBy, ontologyIRI) {
  let baseIRI = ontologyIRI;
  if (baseIRI.endsWith('#')) baseIRI = baseIRI.substring(0, baseIRI.length - 1);
  if (baseIRI.endsWith('/')) baseIRI = baseIRI.substring(0, baseIRI.length - 1);
  if (resourceIRI.indexOf(baseIRI) === 0) return true;
  // OBO?
  if (ontologyIRI.startsWith(OBO_NAMESPACE) && resourceIRI.startsWith(OBO_NAMESPACE)) {
    if (resourceIRI.indexOf("_") > 0) {
      const oboSegment = resourceIRI.substring(0, resourceIRI.indexOf("_")).toLowerCase();
      if (ontologyIRI.startsWith(oboSegment)) return true;
    }
    if (resourceIRI.indexOf("#") > 0) {
      const oboSegment = resourceIRI.substring(0, resourceIRI.indexOf("#")).toLowerCase();
      if (ontologyIRI.startsWith(oboSegment)) return true;
    }
  }
  if (!isDefinedBy) return false;
  if (typeof isDefinedBy !== "string") return false;
  if (isDefinedBy.indexOf(baseIRI) === 0) return true;
  return false;
}

export function computeOntologyIRIoptions(ontologyIRI) {
  let baseIRI = ontologyIRI;
  if (baseIRI.endsWith('#')) baseIRI = baseIRI.substring(0, baseIRI.length - 1);
  if (baseIRI.endsWith('/')) baseIRI = baseIRI.substring(0, baseIRI.length - 1);
  return baseIRI;
}

export function validatePassword(password) {
  return (/^(?=.*[a-z])(?=.*[A-Z])(?=.*[0-9])(?=.*[!@#\\$%\\^&\\*])(?=.{8,50})/.test(password));
}

export function validateEmail(email) {
  return (/^\w+([\.-]?\w+)*@\w+([\.-]?\w+)*(\.\w{2,10})+$/.test(email));
}

export function validateUsername(username) {
  return (/^[a-zA-Z0-9]+$/.test(username));
}

export function displayDate(dateTime, includeTime = false, utc = false) {
  // We're expecting a date time but just in case we get a date!...
  if (dateTime["@value"]) {
    if (dateTime.type === "http://www.w3.org/2001/XMLSchema#date") {
      dateTime = dateTime["@value"];
    }
    else return 'Invalid date';
  }
  const text = new Date(dateTime);
  let dateText = text.getDate() + " " + text.toLocaleString('default', { month: 'long' }) + " " + text.getFullYear();
  if (includeTime) {
    if (utc) {
      dateText += " at " + ("0" + text.getUTCHours()).slice(-2) + ":" +
        ("0" + text.getUTCMinutes()).slice(-2) + ":" +
        ("0" + text.getUTCSeconds()).slice(-2) + " (UTC)";
    }
    else {
      dateText += " at " + text.toLocaleString().substring(12);
    }
  }
  return dateText;
}

export function displayUser(users, user) {
  const userId = Array.isArray(user) ? user[0] : user;
  const userData = users.find(user => user.id === userId);
  const userText = userData && userData.givenName && userData.familyName ?
    userData.givenName + " " + userData.familyName
    :
    userData ?
      userData.username
      :
      "User not available";
  return userText;
}

export function debounce(func, context, args, delay = 500, iteration = 0, maxIterations = 40) {
  // Minimum 50ms
  if (delay < 50) delay = 50;
  if (context.hcTimeout) clearTimeout(context.hcTimeout);
  context.hcTimeout = setTimeout(() => {
    // If we're still searching wait a bit longer
    if (context.state.isSearching && iteration < maxIterations) {
      iteration++;
      debounce(func, context, args, delay, iteration, maxIterations);
    }
    else {
      func(...args);
    }
  }, delay);
}

/**
 * Check basic valus of a string parameter ar met
 * @param {*} param   The parameter to check
 */
export function checkStringParam(param) {
  if (!param || param === undefined || param === "" || typeof param !== "string") return false;
  return true;
}

export function checkParam(param) {
  if (param === 0) return true;
  if (!param || param === undefined || param === "") return false;
  return true;
}

/**
 * Convert a project IRI into its equivalent graph IRI
 * @param {*} projectIRI  The IRI of a project
 */
export function getGraphFromProject(projectIRI) {
  if (!checkStringParam(projectIRI)) throw new Error("An IRI must be supplied to retrieve a graph.");
  if (!checkIRI(projectIRI)) throw new Error("An invalid project IRI has been supplied for graph computation.");
  return "https://grafsync.graphifi.com/graph/project/" + projectIRI.substring(projectIRI.indexOf('/data/project') + 14);
}

export function getProjectFromGraph(graphIRI) {
  if (!checkStringParam(graphIRI)) throw new Error("An IRI must be supplied to retrieve a project.");
  if (!checkIRI(graphIRI)) throw new Error("An invalid graph IRI has been supplied for project computation.");
  return "https://grafsync.graphifi.com/data" + graphIRI.substring(graphIRI.indexOf("/graph") + 6);
}

/**
 * Check if an iri is a valid IRI. We'll keep working on improving this
 * @param {*} iri       An IRI
 * @param {*} allowIPaddress  Indicates if IP addresses should be allowed in check
 */
export function checkIRI(iri, allowIPaddress = false) {
  if (!checkStringParam(iri)) return false;
  if (iri.indexOf(" ") >= 0) return false;
  // Backslash not permitted
  if (iri.indexOf("\\") >= 0) return false;
  // Square brackets not supported
  if (iri.indexOf("[") >= 0) return false;
  if (iri.indexOf("]") >= 0) return false;
  // Double quotes not supported
  if (iri.indexOf('"') >= 0) return false;
  // Braces not supported
  if (iri.indexOf("{") >= 0) return false;
  if (iri.indexOf("}") >= 0) return false;
  if (iri.indexOf("`") >= 0) return false;
  if (iri.indexOf("^") >= 0) return false;
  if (iri.indexOf("~") >= 0) return false;
  if (iri.indexOf("|") >= 0) return false;
  if (iri.indexOf("@") >= 0) return false;
  if (iri.indexOf("<") >= 0) return false;
  if (iri.indexOf(">") >= 0) return false;
  try {
    // If invalid for whatever reason return false (might be partially escaped char for instance)
    decodeURI(iri);
    const url = new URL(iri);
    if (!url.host.includes('.')) return false;
    if (url.hostname.substring(url.hostname.length - 2).includes('.')) return false;
    if (url.hash && url.hash.substring(1).includes('#')) return false;
    if (url.protocol !== "http:" && url.protocol !== "https:") return false;
  }
  catch (e) {
    return false;
  }
  const isIPaddressRegex = RegExp("^https?:\\/\\/[[0-9]");
  let isIPaddress = isIPaddressRegex.test(iri);
  if (isIPaddress) {
    if (!allowIPaddress) return false;
    const IPregex = RegExp("^https?:\\/\\/(?:(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\\.){3}(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\\S*$");
    return IPregex.test(iri);
  }
  return true;
}

export function basicCheckIRI(iri, allowIPaddress = false) {
  if (!checkStringParam(iri)) return false;
  try {
    // If invalid for whatever reason return false (might be partially escaped char for instance)
    decodeURI(iri);
    const url = new URL(iri);
    if (!url.host.includes('.')) return false;
    if (url.hostname.substring(url.hostname.length - 2).includes('.')) return false;
    if (url.hash && url.hash.substring(1).includes('#')) return false;
    if (url.protocol !== "http:" && url.protocol !== "https:") return false;
  }
  catch (e) {
    return false;
  }
  return true;
}

export function detailedCheckIRI(iri) {
  if (!checkStringParam(iri)) return false;
  if (iri.indexOf(" ") >= 0) return false;
  // Backslash not permitted
  if (iri.indexOf("\\") >= 0) return false;
  // Square brackets not supported
  if (iri.indexOf("[") >= 0) return false;
  if (iri.indexOf("]") >= 0) return false;
  // Double quotes not supported
  if (iri.indexOf('"') >= 0) return false;
  // Braces not supported
  if (iri.indexOf("{") >= 0) return false;
  if (iri.indexOf("}") >= 0) return false;
  if (iri.indexOf("`") >= 0) return false;
  if (iri.indexOf("^") >= 0) return false;
  if (iri.indexOf("~") >= 0) return false;
  if (iri.indexOf("|") >= 0) return false;
  if (iri.indexOf("@") >= 0) return false;
  if (iri.indexOf("<") >= 0) return false;
  if (iri.indexOf(">") >= 0) return false;
  if ((iri.indexOf('#') > 0 && (iri.indexOf('#') !== iri.lastIndexOf('#')))
    || iri.toLowerCase().includes('graphifi')
    || iri.toLowerCase().includes('grafsync')) return false;
  try {
    // If invalid for whatever reason return false (might be partially escaped char for instance)
    decodeURI(iri);
    const url = new URL(iri);
    if (!url.host.includes('.')) return false;
    if (url.hostname.substring(url.hostname.length - 2).includes('.')) return false;
    if (url.hash && url.hash.substring(1).includes('#')) return false;
    if (url.protocol !== "http:" && url.protocol !== "https:") return false;
  }
  catch (e) {
    return false;
  }
  return true;
}

export function getDatatypeMenuOptions(exclusionList = []) {
  const menuOptions = Object.keys(DATATYPES)
    .filter(key => !exclusionList.includes(key))
    .map(datatype => {
      return {
        value: DATATYPES[datatype],
        label: DATATYPE_LABELS[DATATYPES[datatype]][DEFAULT_LANG]
      };
    })
    .sort(function (a, b) {
      return a.label < b.label ? -1 : 0;
    });
  return menuOptions;
}

/**
 * Given a string of text compute its datatype if possible
 * @param {*} text  The text to compute a datatype for
 */
export function computeDatatype(text) {
  if (!checkParam(text)) throw new Error("A string must be supplied to compute its datatype.");
  if (typeof text === "boolean") {
    return DATATYPES.BOOLEAN;
  }
  // If it looks likes a number and the existing value is compatible map. We only look fo decimal for now
  else if (typeof text === "number") {
    if (text.toString().indexOf(".") > 0) {
      return DATATYPES.DECIMAL;
    }
    else return DATATYPES.INTEGER;
  }
  return null;
}

/**
 * Check if a string of text matches against a supplied datatype
 * @param {*} text      The text to be validated
 * @param {*} datatype  The IRI of the datatype to check for
 */
export function checkValidForDatatype(text, datatype) {
  if (!checkParam(text)) throw new Error("A string must be supplied for a datatype check.");
  if (!datatype) return true;
  if (DATATYPE_LIMITS[datatype]) {
    const dtInfo = DATATYPE_LIMITS[datatype];
    const regex = dtInfo.expression;
    if (regex) {
      const datatypeRegex = RegExp(regex);
      let isValid = datatypeRegex.test(text);
      // Now we check the value space constraints if any
      if (isValid) {
        if (dtInfo.minimumValue && Number(text) < Number(dtInfo.minimumValue)) isValid = false;
        if (dtInfo.maximumValue && Number(text) > Number(dtInfo.maximumValue)) isValid = false;
      }
      return isValid;
    }
    return true;
  }
  return true;
}


/**
 * 
 * @param {*} text 
 * @param {*} datatype 
 * @param {*} existingValues Will be an array of values or objects of form {text, datatype}
 * @param {*} suppliedMaxLength 
 */
export function checkValidity(text, datatype, existingValues, suppliedMaxLength) {
  let isValid = true;
  // First to a straight lexical comparison (including datatype if applicable)
  let existing = existingValues && existingValues.some(value => {
    if (typeof value === "object") {
      return text === value.text.toString() && (!datatype || datatype === value.datatype);
    }
    else return text === value.toString();
  });
  // If none found there compare value space values if there is a datatype which is numerical in nature
  // This would not match URIs with the same value space value
  if (!existing && datatype) {
    const valueSpaceText = DATATYPE_LIMITS[datatype] && DATATYPE_LIMITS[datatype].maxLength ? Number(text) : null;
    if (valueSpaceText === 0 || valueSpaceText) {
      // Don't allow 00
      if (valueSpaceText === 0 && text !== "0") return false;
      // Don't allow leading zeroes on numbers
      if (valueSpaceText !== 0 && text.charAt(0) === "0") return false;
      existing = existingValues && existingValues.some(value => {
        if (typeof value === "object") {
          return valueSpaceText === Number(value.text.toString()) && datatype === value.datatype;
        }
        else return valueSpaceText === Number(value.toString());
      });
    }
  }
  if (isValid) isValid = checkValidForDatatype(text, datatype);
  const maxLength = datatype && DATATYPE_LIMITS[datatype] && DATATYPE_LIMITS[datatype].maxLength ?
    DATATYPE_LIMITS[datatype].maxLength
    :
    suppliedMaxLength;
  if (text.length > maxLength) isValid = false;
  return { isValid, existing };
}

/**
 * Check if a string is at the maximum length and generate a warning.
 * @param {*} text    The string to check
 * @param {*} value   The length to check for
 */
export function createLengthWarning(text, value, isEdited = true) {
  if (text.length >= value && isEdited) return <span data-test="helper-text">Maximum is {value} characters.</span>;
  return null;
}

export function createLengthWarning2(text, value, isEdited = true) {
  if ((text.length >= value && isEdited) || (text.length > value && !isEdited)) return <span data-test="helper-text">Maximum is {value} characters.</span>;
  return null;
}

export function checkEntitlements(userPermissions, needed) {
  const isPermitted = userPermissions.some(userEnt => needed.includes(userEnt)) || false;
  return isPermitted;
}

// This is not used
export function checkAccessEntitlements(userPermissions, needed) {
  if (!userPermissions) return false;
  const isPermitted = userPermissions.every(userEnt => needed.includes(userEnt)) || false;
  return isPermitted;
}

// The entitlements on a resource will be project specific
// Note if a resources is newly created it won't have hasEntitlement as only gets added on loading
export function checkResourceEntitlements(resource, needed) {
  if (GS.isReadOnly()) return true;
  // If about to be deleted it is read only
  if (resource.pendingDeletion) return true;
  // If no entitlement information by default writeable (i.e. created by user)
  if (!resource.hasEntitlement) return false;
  const neededPermissions = Array.isArray(needed) ? needed : [needed];
  const isPermitted = resource.hasEntitlement.some(userEnt => neededPermissions.includes(userEnt)) || false;
  return !isPermitted;
}

// Expect an array of language codes and get back an array of langcode + lang in required language
export function getOrderedLanguages(langCodes, lang) {
  const subset = langCodes.map(code => {
    return { code: code, label: LANGS[code][lang] };
  });
  return sortByProp(subset, "label");
}

export function getAllOrderedLanguages(lang) {
  const subset = Object.keys(LANGS).map(code => {
    return { code: code, label: LANGS[code][lang] };
  });
  return sortByProp(subset, "label");
}

export function getAllLanguageCodes() {
  return Object.keys(LANGS);
}

export function processHashLocation(location) {

  const paramsMap = new Map();
  const projectParamsText = location.hash.substring(1);
  const projectParamsList = projectParamsText.split('&');
  if (projectParamsList) {
    projectParamsList.forEach(text => {
      const equalsIndex = text.indexOf("=");
      const keyText = text.substring(0, equalsIndex);
      const keyValue = text.substring(equalsIndex + 1);
      paramsMap.set(keyText, keyValue);
    });
  }
  return paramsMap;
}

export function sortObjects(objects, property, lang = "en", secondLang, caseSensitive = true) {
  return objects.sort(function (a, b) {
    let orderA = a.id.includes('#') ?
      decodeURIComponent(a.id.substring(a.id.lastIndexOf('#') + 1))
      :
      decodeURIComponent(a.id.substring(a.id.lastIndexOf('/') + 1));
    let orderB = b.id.includes('#') ?
      decodeURIComponent(b.id.substring(b.id.lastIndexOf('#') + 1))
      :
      decodeURIComponent(b.id.substring(b.id.lastIndexOf('/') + 1));
    if (a[property]) {
      a[property][lang] === undefined ? orderA = a.id : orderA = a[property][lang];
      if (orderA === a.id && secondLang) {
        a[property][secondLang] === undefined ? orderA = a.id : orderA = a[property][secondLang];
        if (orderA === a.id) {
          a[property]["und"] === undefined ? orderA = a.id : orderA = a[property]["und"];
        }
      }
    }
    if (b[property]) {
      b[property][lang] === undefined ? orderB = b.id : orderB = b[property][lang];
      if (orderB === b.id && secondLang) {
        b[property][secondLang] === undefined ? orderB = b.id : orderB = b[property][secondLang];
        if (orderB === b.id) {
          b[property]["und"] === undefined ? orderB = b.id : orderB = b[property]["und"];
        }
      }
    }
    if (Array.isArray(orderA)) orderA = orderA.sort().join("");
    if (Array.isArray(orderB)) orderB = orderB.sort().join("");
    if (caseSensitive) {
      if (orderA < orderB) return -1;
      if (orderA > orderB) return 1;
    }
    else {
      if (orderA.toLowerCase() < orderB.toLowerCase()) return -1;
      if (orderA.toLowerCase() > orderB.toLowerCase()) return 1;
    }
    return 0;
  });
}

export function sortByLength(objects, property, lang = "en") {
  return objects.sort(function (a, b) {
    let orderA = 0;
    let orderB = 0;
    if (a[property]) {
      a[property][lang] === undefined ? orderA = 0 : orderA = a[property][lang].length;
    }
    if (b[property]) {
      b[property][lang] === undefined ? orderB = 0 : orderB = b[property][lang].length;
    }
    if (orderA < orderB) return -1;
    if (orderA > orderB) return 1;
    let orderA1 = a[property] && a[property][lang] ? a[property][lang] : "";
    let orderB1 = b[property] && b[property][lang] ? b[property][lang] : "";
    if (orderA1 < orderB1) return -1;
    if (orderA1 > orderB1) return 1;
    return 0;
  });
}

export function sortByProp(objects, property, secondProperty, caseSensitive = true) {
  return objects.sort(function (a, b) {
    let orderA = "";
    let orderB = "";
    a[property] === undefined ? orderA = "" : orderA = a[property];
    b[property] === undefined ? orderB = "" : orderB = b[property];
    if (caseSensitive) {
      if (orderA < orderB) return -1;
      if (orderA > orderB) return 1;
    }
    else {
      if (orderA.toLowerCase() < orderB.toLowerCase()) return -1;
      if (orderA.toLowerCase() > orderB.toLowerCase()) return 1;
    }
    if (orderA === orderB && secondProperty) {
      a[secondProperty] === undefined ? orderA = "" : orderA = a[secondProperty];
      b[secondProperty] === undefined ? orderB = "" : orderB = b[secondProperty];
      if (orderA < orderB) return -1;
      if (orderA > orderB) return 1;
    }
    return 0;
  });
}

export function reverseSortByProp(objects, property) {
  return objects.sort(function (a, b) {
    let orderA = "";
    let orderB = "";
    a[property] === undefined ? orderA = "" : orderA = a[property];
    b[property] === undefined ? orderB = "" : orderB = b[property];
    if (orderA < orderB) return 1;
    if (orderA > orderB) return -1;
    return 0;
  });
}

export function sortByLangProp(objects, firstProperty, secondProperty, lang = "en", secondLang) {
  return objects.sort(function (a, b) {
    let orderA = a.id;
    let orderB = b.id;
    let aProperty = firstProperty;
    let bProperty = firstProperty;
    if (!a[firstProperty]) aProperty = secondProperty;
    if (!b[firstProperty]) bProperty = secondProperty;
    let aLangToUse = lang;
    if (a[aProperty] && !a[aProperty][lang]) {
      aLangToUse = secondLang;
      if (!a[aProperty][secondLang]) aLangToUse = "und";
    }
    let bLangToUse = lang;
    if (b[bProperty] && !b[bProperty][lang]) {
      bLangToUse = secondLang;
      if (!b[bProperty][secondLang]) bLangToUse = "und";
    }
    orderA = a[aProperty] !== undefined && a[aProperty][aLangToUse] ?
      a[aProperty][aLangToUse]
      :
      a.id;
    orderB = b[bProperty] !== undefined && b[bProperty][bLangToUse] ?
      b[bProperty][bLangToUse]
      :
      b.id;
    if (Array.isArray(orderA)) orderA = orderA.sort().join("");
    if (Array.isArray(orderB)) orderB = orderB.sort().join("");
    if (orderA.toLowerCase() < orderB.toLowerCase()) return -1;
    if (orderA.toLowerCase() > orderB.toLowerCase()) return 1;
    return 0;
  });
}

export const getLabelOrID = (iri, data, prop, lang) => {
  if (!data) return iri;
  if (lang) {
    const label = data && data[prop] ?
      data[prop][lang] ?
        data[prop][lang]
        :
        data[prop]["und"] ?
          data[prop]["und"]
          :
          data.id
      :
      data.id;
    return label;
  }
  else {
    const label = data && data[prop] ?
      data[prop]
      :
      data.id;
    return label;
  }
};

// A common method to sort a set of IRIs where the data is loaded into GS
export const sortResources = (resources, graph, prop, lang) => {
  const sortedResources = [...resources].sort((a, b) => {
    const aData = GS.data(a, graph, false);
    const bData = GS.data(b, graph, false);
    const aLabel = getLabelOrID(a, aData, prop, lang);
    const bLabel = getLabelOrID(b, bData, prop, lang);
    if (aLabel.toLowerCase() < bLabel.toLowerCase()) return -1;
    if (bLabel.toLowerCase() < aLabel.toLowerCase()) return 1;
    return 0;
  });
  return sortedResources;
};

// A common method to sort a set of IRIs where the data is loaded into GS
export const sortByCreated = (resources, graph, ascending = true) => {
  const sortedResources = [...resources].sort((a, b) => {
    const aData = GS.data(a, graph, false);
    const bData = GS.data(b, graph, false);
    const aLabel = aData ? aData.created || aData._created || aData.id : a;
    const bLabel = bData ? bData.created || bData._created || bData.id : b;
    if (aLabel.toLowerCase() < bLabel.toLowerCase()) return -1;
    if (bLabel.toLowerCase() < aLabel.toLowerCase()) return 1;
    return 0;
  });
  return sortedResources;
};

/**
 * A helper function to manage cancellation of asynchronous promises
 * @param {*} promise     The promise to wrap
 * @param {*} reference   A reference that can be used to identify the promise 
 */
export const makeCancelable = (promise, reference) => {
  let hasCanceled_ = false;

  const wrappedPromise = new Promise((resolve, reject) => {
    promise.then(
      val => hasCanceled_ ? reject({ isCanceled: true, reference: reference }) : resolve(val),
      error => hasCanceled_ ? reject({ isCanceled: true, reference: reference }) : reject(error)
    );
  });

  return {
    promise: wrappedPromise,
    isCancelled() { return hasCanceled_; },
    cancel() {
      hasCanceled_ = true;
    },
  };
};

/**
 * We don't allow leading spaces or tabs or other chunk
 * @param {*} text 
 */
export const sanitiseText = (text) => {
  if (!text) return "";
  text = text.trimLeft();
  text = text.replace(/\t/g, "");
  text = text.replace(/\n/g, "");
  text = text.replace(/\r/g, "");
  text = text.replace(/\f/g, "");
  return text;
};

export const lightSanitiseText = (text) => {
  if (!text) return "";
  text = text.trimLeft();
  text = text.replace(/\t/g, "");
  text = text.replace(/\r/g, "");
  text = text.replace(/\f/g, "");
  return text;
};

export const fullySanitiseText = (text) => {
  if (!text) return "";
  text = text.trimLeft();
  text = text.replace(/\t/g, "");
  text = text.replace(/\n/g, "");
  text = text.replace(/\r/g, "");
  text = text.replace(/\f/g, "");
  text = text.replace(/\\/g, "");
  text = text.replace(/"/g, "");
  text = text.replace(/'/g, "");
  return text;
};

// Allow a user to open a link in a new browser tab
export function getNewTabLink(link) {
  const tabLink = "?graphologi-target=" + GS.getActiveSubscriptionBucket() + "&subscription=" + GS.getActiveSubscription() + "#";
  const withTabLink1 = link.substring(link, link.indexOf("#"));
  const withTabLink2 = link.substring(link.indexOf("#") + 1);
  const fullTabLink = withTabLink1 + tabLink + withTabLink2;
  window.open(window.location.origin + "/gs" + fullTabLink);
}
